import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState({
    isActive: false,
    hasInitiation: false,
    status: 'none',
    paymentStatus: 'active',
    gracePeriodEnd: null,
    paymentFailureReason: null,
    needsPaymentUpdate: false,
    loading: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchSubscriptionStatus();
    } else {
      setSubscription({
        isActive: false,
        hasInitiation: false,
        status: 'none',
        paymentStatus: null,
        gracePeriodEnd: null,
        paymentFailureReason: null,
        needsPaymentUpdate: false,
        loading: false
      });
    }
  }, [isAuthenticated, token]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription({
        isActive: response.data.is_active,
        hasInitiation: response.data.has_initiation,
        status: response.data.subscription_status,
        paymentStatus: response.data.payment_status,
        gracePeriodEnd: response.data.grace_period_end,
        paymentFailureReason: response.data.payment_failure_reason,
        lastPaymentFailure: response.data.last_payment_failure,
        needsPaymentUpdate: response.data.needs_payment_update,
        accessReason: response.data.access_reason,
        nextBillingDate: response.data.next_billing_date,
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(prev => ({ ...prev, loading: false }));
    }
  };

  const refreshSubscription = () => {
    if (isAuthenticated && token) {
      fetchSubscriptionStatus();
    }
  };

  // Check if user has full access (active subscription or within grace period)
  const hasFullAccess = subscription.isActive && subscription.hasInitiation;

  // Check if user can preview (logged in but not subscribed)
  const isPreviewMode = isAuthenticated && !hasFullAccess;

  // Check if payment is past due (needs attention but still has access during grace period)
  const isPaymentPastDue = subscription.paymentStatus === 'past_due';

  // Check if access is suspended due to payment failure
  const isAccessSuspended = subscription.paymentStatus === 'suspended';

  return (
    <SubscriptionContext.Provider value={{ 
      ...subscription, 
      hasFullAccess,
      isPreviewMode,
      isPaymentPastDue,
      isAccessSuspended,
      refreshSubscription 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
